import React from "react";
import { X } from "lucide-react";

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-bold text-[#402B69]">Terms & Conditions</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 text-sm text-gray-600 leading-relaxed space-y-4">
          <p>
            These Terms & Conditions govern your subscription to Glossa services.
            By proceeding with this business onboarding, you agree to be bound by these terms.
          </p>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">1. Service Agreement</h3>
            <p>
              Glossa provides salon and wellness business management software, including
              booking, POS, customer management, reporting, and related modules as described
              in your selected package.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">2. Subscription & Billing</h3>
            <p>
              Your subscription renews automatically on a monthly or yearly basis according
              to the billing cycle shown during onboarding. Fees are charged in advance unless
              otherwise stated. You authorise Glossa to charge your selected payment method
              for recurring subscription fees and applicable taxes.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">3. Account Responsibilities</h3>
            <p>
              You are responsible for maintaining accurate business information, safeguarding
              login credentials, and ensuring that users within your organisation comply with
              applicable laws when using the platform.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">4. Fair Use & Availability</h3>
            <p>
              Glossa aims to provide reliable service but does not guarantee uninterrupted
              availability. Planned maintenance, third-party outages, or force majeure events
              may affect access. Glossa may update features to improve the platform.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">5. Cancellation</h3>
            <p>
              You may cancel your subscription in accordance with your agreement. Cancellation
              stops future billing but does not entitle you to a refund for the current billing
              period unless required by law or explicitly agreed in writing.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">6. Limitation of Liability</h3>
            <p>
              To the fullest extent permitted by law, Glossa is not liable for indirect,
              incidental, or consequential losses arising from use of the service. Glossa&apos;s
              total liability is limited to the fees paid for the subscription period in which
              the claim arose.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">7. Governing Law</h3>
            <p>
              These terms are governed by the laws of England and Wales. Any disputes shall be
              subject to the exclusive jurisdiction of the courts of England and Wales unless
              otherwise required by mandatory local law.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-2.5 bg-[#885ABB] hover:bg-[#7647a8] text-white rounded-lg font-semibold text-sm transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
